import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";

type PayPalButtonsInstance = {
  render: (el: HTMLElement) => Promise<void>;
  close?: () => Promise<void>;
};

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: Record<string, unknown>) => PayPalButtonsInstance;
    };
  }
}

/**
 * PayPal Subscription Buttons (SDK) — DOC 02 § 4.6.
 * createSubscription usa plan_id provisionado no backend; onApprove devolve I-xxx ao parent.
 * Sem clientId real (Fake/local): botão fallback.
 */
interface PayPalSubscribeButtonProps {
  clientId?: string | null;
  planId: number;
  /** Busca paypal_plan_id no backend (cupom já aplicado no prepare). */
  onPrepare: () => Promise<{ paypal_plan_id: string; client_id?: string }>;
  /** subscriptionID do PayPal após approve. */
  onApproved: (subscriptionId: string) => Promise<void>;
  onError?: (message: string) => void;
  disabled?: boolean;
  label?: string;
  /** Fallback sem SDK. */
  onPayFallback?: () => void;
}

const PayPalSubscribeButton: React.FC<PayPalSubscribeButtonProps> = ({
  clientId,
  onPrepare,
  onApproved,
  onError,
  disabled = false,
  label,
  onPayFallback,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loadingSdk, setLoadingSdk] = useState(false);
  const [sdkFailed, setSdkFailed] = useState(false);

  const canUseSdk =
    !!clientId &&
    clientId.length > 8 &&
    !clientId.startsWith("sb-fake") &&
    !disabled;

  useEffect(() => {
    if (!canUseSdk || !containerRef.current) {
      return;
    }

    let cancelled = false;
    let buttons: PayPalButtonsInstance | null = null;
    const container = containerRef.current;

    const load = async () => {
      setLoadingSdk(true);
      setSdkFailed(false);
      try {
        await loadPayPalSdk(clientId!);
        if (cancelled || !window.paypal || !container) {
          return;
        }
        container.innerHTML = "";
        buttons = window.paypal.Buttons({
          style: {
            layout: "vertical",
            color: "gold",
            shape: "rect",
            label: "subscribe",
          },
          createSubscription: async (_data: unknown, actions: {
            subscription: {
              create: (opts: { plan_id: string }) => Promise<string>;
            };
          }) => {
            const prepared = await onPrepare();
            return actions.subscription.create({
              plan_id: prepared.paypal_plan_id,
            });
          },
          onApprove: async (data: { subscriptionID?: string }) => {
            const id = data?.subscriptionID;
            if (!id) {
              onError?.("PayPal did not return a subscription id.");
              return;
            }
            await onApproved(id);
          },
          onError: (err: unknown) => {
            const msg =
              err instanceof Error ? err.message : "PayPal checkout failed.";
            onError?.(msg);
          },
        });
        await buttons.render(container);
      } catch (e) {
        setSdkFailed(true);
        onError?.(
          e instanceof Error ? e.message : "Could not load PayPal Buttons."
        );
      } finally {
        if (!cancelled) setLoadingSdk(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      void buttons?.close?.();
      if (container) container.innerHTML = "";
    };
  }, [canUseSdk, clientId, onPrepare, onApproved, onError]);

  if (!canUseSdk || sdkFailed) {
    return (
      <div>
        {sdkFailed && (
          <Alert color="warning" className="py-2">
            PayPal Buttons unavailable — use the button below.
          </Alert>
        )}
        <Button
          color="primary"
          className="d-flex align-items-center gap-2"
          disabled={disabled}
          onClick={() => onPayFallback?.()}
        >
          <i className="ri-paypal-fill" style={{ fontSize: "1.2rem" }} />
          <span>{label || "PayPal"}</span>
        </Button>
      </div>
    );
  }

  return (
    <div>
      {loadingSdk && (
        <div className="mb-2 text-muted small">
          <Spinner size="sm" className="me-1" /> Loading PayPal…
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
};

const sdkPromises: Record<string, Promise<void> | undefined> = {};

function loadPayPalSdk(clientId: string): Promise<void> {
  const src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
    clientId
  )}&vault=true&intent=subscription&currency=USD`;

  if (window.paypal) {
    return Promise.resolve();
  }
  const cached = sdkPromises[clientId];
  if (cached) {
    return cached;
  }

  sdkPromises[clientId] = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[data-paypal-sdk="${clientId}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("PayPal SDK load failed"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.paypalSdk = clientId;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("PayPal SDK load failed"));
    document.body.appendChild(script);
  });

  return sdkPromises[clientId]!;
}

export default PayPalSubscribeButton;
